import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaPlane, FaTimes, FaSortAmountDown } from "react-icons/fa";
import FlightCard from "./FlightCard";
import AdvancedFlightFilters from "./AdvancedFlightFilters";

// Lazy load batch size – render this many cards at a time for smooth scroll
const INITIAL_BATCH = 18;
const LOAD_MORE_BATCH = 18;
const LOAD_MORE_DEBOUNCE_MS = 200;

const FlightResults = ({
  showResults,
  setShowResults,
  searchResults,
  combinedResults, // Changed from searchResults to combinedResults
  pagedResults,
  currentPage,
  setCurrentPage,
  totalPages,
  pageSize,
  tripType,
  expandedFlights,
  toggleFlightDetails,
  setSelectedFlightForPrice,
  setShowPriceModal,
  verifyFlightPrice,
  fetchSSROptions,
  bookWithSSR,
  statusRefdId,
  onwardRefID,
  returnRefId,
  handleAuthCheck,
  serType,
  formatApiDateTime,
  minutesToHm,
  getSegments,
  getStopsText,
  getTotalDurationMinutes,
  sortBy,
  setSortBy,
  totalPaxCount = 1,
}) => {
  const [filteredFlights, setFilteredFlights] = useState(combinedResults || []);

  // Update filtered flights when combinedResults change
  useEffect(() => {
    setFilteredFlights(combinedResults || []);
  }, [combinedResults]);

  if (!showResults) return null;

  const flightsToDisplay = filteredFlights.length > 0 ? filteredFlights : (combinedResults || []);

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4">
        <ResultsHeader
          searchResults={flightsToDisplay}
          setShowResults={setShowResults}
          tripType={tripType}
          sortBy={sortBy}
          setSortBy={setSortBy}
          onwardCount={searchResults?.onward?.length}
          returnCount={searchResults?.return?.length}
        />

        {/* Two-column layout: Filters on left, Flights on right */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
          {/* Left Sidebar - Filters */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <AdvancedFlightFilters
                flights={combinedResults || []}
                filteredFlights={filteredFlights}
                setFilteredFlights={setFilteredFlights}
              />
            </div>
          </div>

          {/* Right Side - Flight Results */}
          <div className="lg:col-span-3">
            {flightsToDisplay?.length === 0 ? (
              <NoResults />
            ) : (
              <ResultsList
                pagedResults={pagedResults}
                tripType={tripType}
                serType={serType}
                expandedFlights={expandedFlights}
                toggleFlightDetails={toggleFlightDetails}
                setSelectedFlightForPrice={setSelectedFlightForPrice}
                setShowPriceModal={setShowPriceModal}
                verifyFlightPrice={verifyFlightPrice}
                fetchSSROptions={fetchSSROptions}
                bookWithSSR={bookWithSSR}
                statusRefdId={statusRefdId}
                onwardRefID={onwardRefID}
                returnRefId={returnRefId}
                handleAuthCheck={handleAuthCheck}
                filteredFlights={filteredFlights}
                combinedResults={combinedResults}
                formatApiDateTime={formatApiDateTime}
                minutesToHm={minutesToHm}
                getSegments={getSegments}
                getStopsText={getStopsText}
                getTotalDurationMinutes={getTotalDurationMinutes}
                totalPaxCount={totalPaxCount}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultsHeader = ({
  searchResults,
  setShowResults,
  tripType,
  sortBy,
  setSortBy,
  onwardCount,
  returnCount,
}) => {
  // For round trip: show addition (onward + return) instead of multiplication (combinations)
  const displayCount = tripType === 1 && (onwardCount != null || returnCount != null)
    ? `${onwardCount || 0} onward + ${returnCount || 0} return = ${(onwardCount || 0) + (returnCount || 0)} total flights`
    : `${searchResults?.length || 0} found`;
  
  return (
  <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
        Available Flights ({displayCount})
        {tripType === 1 && " (Round Trip)"}
      </h2>
      <p className="text-gray-600 text-xs sm:text-sm mt-1">
        {tripType === 1
          ? `Showing ${searchResults?.length || 0} round trip combinations (${onwardCount || 0} onward × ${returnCount || 0} return)`
          : "Showing one-way flights"}
      </p>
    </div>

    <div className="flex items-center gap-4">
      <button
        onClick={() => setShowResults(false)}
        className="text-gray-500 hover:text-gray-700 p-2"
      >
        <FaTimes className="text-xl" />
      </button>
    </div>
  </div>
  );
};

const NoResults = () => (
  <div className="text-center py-16">
    <FaPlane className="text-6xl text-gray-400 mx-auto mb-4 opacity-50" />
    <h3 className="text-2xl font-semibold text-gray-600 mb-2">
      No Flights Found
    </h3>
    <p className="text-gray-500 max-w-md mx-auto">
      We couldn't find any flights matching your search criteria. Try adjusting
      your dates or search parameters.
    </p>
  </div>
);

const ResultsList = ({
  pagedResults,
  tripType,
  serType,
  expandedFlights,
  toggleFlightDetails,
  setSelectedFlightForPrice,
  setShowPriceModal,
  verifyFlightPrice,
  fetchSSROptions,
  bookWithSSR,
  statusRefdId,
  onwardRefID,
  returnRefId,
  handleAuthCheck,
  formatApiDateTime,
  minutesToHm,
  getSegments,
  getStopsText,
  getTotalDurationMinutes,
  filteredFlights,
  combinedResults,
  totalPaxCount = 1,
}) => {
  const flightsToDisplay = (filteredFlights && filteredFlights.length > 0)
    ? filteredFlights
    : (combinedResults || []);

  const totalCount = flightsToDisplay.length;
  const listKey = totalCount ? (flightsToDisplay[0]?.id ?? String(0)) : "";
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(INITIAL_BATCH, totalCount)
  );
  const loadMoreDebounceRef = useRef(0);

  // Reset visible count only when result set or filters change (not on every re-render)
  useEffect(() => {
    setVisibleCount(Math.min(INITIAL_BATCH, totalCount));
  }, [totalCount, listKey]);

  // Sentinel: when this is in view, load more cards (debounced)
  const loadMore = useCallback(() => {
    const now = Date.now();
    if (now - loadMoreDebounceRef.current < LOAD_MORE_DEBOUNCE_MS) return;
    loadMoreDebounceRef.current = now;
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, totalCount));
  }, [totalCount]);

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    rootMargin: "120px 0px 0px 0px",
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView && visibleCount < totalCount) loadMore();
  }, [inView, visibleCount, totalCount, loadMore]);

  const visibleFlights = flightsToDisplay.slice(0, visibleCount);
  const hasMore = visibleCount < totalCount;

  return (
    <div className="space-y-4">
      {visibleFlights.map((flight, index) => (
        <motion.div
          key={`${flight.id || index}-${flight.isRoundTrip ? "round" : "oneway"}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <FlightCard
            flight={flight}
            serType={serType}
            index={index}
            tripType={tripType}
            expandedFlights={expandedFlights}
            toggleFlightDetails={toggleFlightDetails}
            setSelectedFlightForPrice={setSelectedFlightForPrice}
            setShowPriceModal={setShowPriceModal}
            verifyFlightPrice={verifyFlightPrice}
            fetchSSROptions={fetchSSROptions}
            bookWithSSR={bookWithSSR}
            statusRefdId={statusRefdId}
            onwardRefID={onwardRefID}
            returnRefId={returnRefId}
            handleAuthCheck={handleAuthCheck}
            formatApiDateTime={formatApiDateTime}
            minutesToHm={minutesToHm}
            getSegments={getSegments}
            getStopsText={getStopsText}
            getTotalDurationMinutes={getTotalDurationMinutes}
            totalPaxCount={totalPaxCount}
          />
        </motion.div>
      ))}
      {/* Sentinel: when this enters viewport, load next batch */}
      {hasMore && (
        <div ref={sentinelRef} className="h-4 flex items-center justify-center py-6">
          <span className="text-gray-400 text-sm">Loading more flights…</span>
        </div>
      )}
    </div>
  );
};

const Pagination = ({
  searchResults,
  pageSize,
  currentPage,
  setCurrentPage,
  totalPages,
}) => {
  if (!searchResults || searchResults.length <= pageSize) return null;

  // Function to render page numbers with ellipsis
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-2 pt-8 mt-8 border-t border-gray-200">
      <button
        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 bg-white hover:bg-gray-50 disabled:hover:bg-white flex items-center gap-2 transition-colors"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        <span className="hidden sm:inline">Previous</span>
      </button>

      <div className="flex items-center gap-1">
        {renderPageNumbers().map((pageNum, i) =>
          pageNum === "..." ? (
            <span key={`ellipsis-${i}`} className="px-3 py-2 text-gray-500">
              ...
            </span>
          ) : (
            <button
              key={pageNum}
              className={`w-10 h-10 flex items-center justify-center border rounded-lg transition-colors ${
                currentPage === pageNum
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setCurrentPage(pageNum)}
            >
              {pageNum}
            </button>
          )
        )}
      </div>

      <button
        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 bg-white hover:bg-gray-50 disabled:hover:bg-white flex items-center gap-2 transition-colors"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      >
        <span className="hidden sm:inline">Next</span>
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>
  );
};

export default FlightResults;
